<script lang="ts">
	import { Button } from '$lib/components/ui/button/index.js';
	import * as Command from '$lib/components/ui/command/index.js';
	import * as Popover from '$lib/components/ui/popover/index.js';
	import { cn } from '$lib/utils.js';
	import type { ClassValue } from 'clsx';
	import { getPortsState } from '@/stores/Port.svelte';
	import { Check, ChevronsUpDown } from '@lucide/svelte';
	import { tick } from 'svelte';

	interface Props {
		bindableProp?: string;
		triggerClassName?: ClassValue;
		placeholder?: string;
		invalid?: boolean;
		invalidDescribedBy?: string;
		id?: string;
		onselect?: () => void;
	}

	let {
		bindableProp = $bindable(),
		triggerClassName,
		placeholder = 'Busca un puerto de salida...',
		invalid = false,
		invalidDescribedBy,
		id,
		onselect
	}: Props = $props();

	let searchList: { value: string; label: string }[] = $state([]);
	const ports = getPortsState();

	// Fetch ports data when component mounts
	$effect(() => {
		ports.fetchPorts();
	});

	$effect(() => {
		if (searchList.length === 0 && ports.ports.length > 0) {
			extractLocationsFromPorts();
		}
	});

	function extractLocationsFromPorts() {
		const portNames = ports.ports.map((port) => port.name.trim());
		const portProvinces = ports.ports.map((port) => port.province.trim());
		const uniqueLocations = [...new Set([...portNames, ...portProvinces])];
		searchList = uniqueLocations.map((location) => ({
			value: location,
			label: location
		}));
	}

	let open = $state(false);
	let value = $state('');
	let triggerRef = $state<HTMLButtonElement>(null!);

	// Inicializar el valor basado en bindableProp cuando los puertos estén cargados
	$effect(() => {
		if (bindableProp && ports.ports.length > 0 && !value) {
			const currentPort = ports.ports.find((port) => port.id === bindableProp);
			if (currentPort) {
				value = currentPort.name.trim();
			}
		}
	});

	const selectedValue = $derived(searchList.find((f) => f.value === value)?.label);

	$effect(() => {
		if (selectedValue == null || selectedValue === '') return;
		const selectedPort = ports.ports.find((port) => port.name.trim() === selectedValue);
		if (selectedPort) bindableProp = selectedPort.id;
	});

	// We want to refocus the trigger button when the user selects
	// an item from the list so users can continue navigating the
	// rest of the form with the keyboard.
	function closeAndFocusTrigger() {
		open = false;
		tick().then(() => {
			triggerRef.focus();
		});
	}
</script>

<Popover.Root bind:open={open}>
	<Popover.Trigger bind:ref={triggerRef}>
		{#snippet child({ props })}
			{@const { class: triggerClass, ...triggerProps } = props}
			<Button
				{id}
				type="button"
				variant="outline"
				{...triggerProps}
				class={cn(
					triggerClass as ClassValue,
					triggerClassName ??
						'h-8 min-h-8 w-full justify-between px-2.5 text-base font-normal md:text-sm',
					invalid && 'border-red-500 focus-visible:border-red-500 focus-visible:ring-red-500/10'
				)}
				role="combobox"
				aria-expanded={open}
				aria-invalid={invalid ? true : undefined}
				aria-describedby={invalid && invalidDescribedBy ? invalidDescribedBy : undefined}
			>
				<span
					class={cn('truncate text-left', !selectedValue && 'text-zp-muted')}
				>{selectedValue || placeholder}</span>
				<ChevronsUpDown class="ml-2 size-4 shrink-0 opacity-50" />
			</Button>
		{/snippet}
	</Popover.Trigger>
	<Popover.Content class="w-[300px] p-0">
		<Command.Root>
			<Command.Input placeholder="Buscar puerto..." />
			<Command.List>
				<Command.Empty>No se ha encontrado ningún puerto</Command.Empty>
				<Command.Group>
					{#each searchList as framework (framework.value)}
						<Command.Item
							value={framework.value}
							onSelect={() => {
								value = framework.value;
								closeAndFocusTrigger();
								onselect?.();
							}}
						>
							<Check class={cn('mr-2 size-4', value !== framework.value && 'text-transparent')} />
							{framework.label}
						</Command.Item>
					{/each}
				</Command.Group>
			</Command.List>
		</Command.Root>
	</Popover.Content>
</Popover.Root>
